import React, { useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import {
  Building,
  MapPin,
  Phone,
  Mail,
  Globe,
  Upload,
  User,
  FileText,
} from "lucide-react";
import ProductSection from "../../ProductsModule/Components/ProductSection";

const CompanySettings = () => {
  const [logoFile, setLogoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    "https://via.placeholder.com/80",
  );

  // Sample company data
  const [companyInfo, setCompanyInfo] = useState({
    name: "Acme Wholesalers Pvt. Ltd.",
    legalName: "Acme Wholesale Distribution Private Limited",
    gstNumber: "27AABCT3518Q1ZV",
    panNumber: "AABCT3518Q",
    email: "contact@acmewholesalers.com",
    phone: "+91 98765 43210",
    website: "www.acmewholesalers.com",
    industry: "FMCG & Grocery",
    businessType: "private_limited",
    yearEstablished: "2015",
    numberOfEmployees: "51-100",
  });

  // Sample address data
  const [addressInfo, setAddressInfo] = useState({
    street: "123 Business Park, Andheri East",
    city: "Mumbai",
    state: "Maharashtra",
    pincode: "400069",
    country: "India",
  });

  // Sample bank details
  const [bankInfo, setBankInfo] = useState({
    accountName: "Acme Wholesalers Pvt. Ltd.",
    accountNumber: "1234567890123456",
    bankName: "HDFC Bank",
    branchName: "Andheri East Branch",
    ifscCode: "HDFC0001234",
    accountType: "current",
  });

  const handleLogoChange = e => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCompanyInfoChange = e => {
    const { name, value } = e.target;
    setCompanyInfo({
      ...companyInfo,
      [name]: value,
    });
  };

  const handleAddressInfoChange = e => {
    const { name, value } = e.target;
    setAddressInfo({
      ...addressInfo,
      [name]: value,
    });
  };

  const handleBankInfoChange = e => {
    const { name, value } = e.target;
    setBankInfo({
      ...bankInfo,
      [name]: value,
    });
  };

  return (
    <div className="space-y-8">
      {/* Company Information Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex items-center mb-4">
          <Building size={20} className="text-[#5652B0] mr-2" />
          <ProductSection data={"Company Information"} />
        </div>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, fontFamily: "Outfit" }}>
          Update your company profile and business information
        </Typography>

        <div className="flex flex-col md:flex-row gap-8 mb-6">
          <div className="flex flex-col items-center">
            <Avatar
              src={previewUrl}
              alt="Company Logo"
              sx={{ width: 120, height: 120, mb: 2 }}
            />
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="logo-upload"
              type="file"
              onChange={handleLogoChange}
            />
            <label htmlFor="logo-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<Upload size={16} />}
                size="small"
                sx={{
                  textTransform: "none",
                  borderColor: "#5652B0",
                  color: "#5652B0",
                  fontFamily: "Outfit",
                  "&:hover": {
                    borderColor: "#4A47A3",
                    backgroundColor: "rgba(86, 82, 176, 0.04)",
                  },
                }}>
                Upload Logo
              </Button>
            </label>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, fontFamily: "Outfit", textAlign: "center" }}>
              Recommended: 400x400px PNG or JPG
            </Typography>
          </div>

          <div className="flex-1">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Company Name"
                  name="name"
                  value={companyInfo.name}
                  onChange={handleCompanyInfoChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#5652B0",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#5652B0",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Legal Business Name"
                  name="legalName"
                  value={companyInfo.legalName}
                  onChange={handleCompanyInfoChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#5652B0",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#5652B0",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="GST Number"
                  name="gstNumber"
                  value={companyInfo.gstNumber}
                  onChange={handleCompanyInfoChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#5652B0",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#5652B0",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="PAN Number"
                  name="panNumber"
                  value={companyInfo.panNumber}
                  onChange={handleCompanyInfoChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#5652B0",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#5652B0",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </div>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Business Email"
              name="email"
              value={companyInfo.email}
              onChange={handleCompanyInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={16} className="text-gray-500" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Phone Number"
              name="phone"
              value={companyInfo.phone}
              onChange={handleCompanyInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone size={16} className="text-gray-500" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Website"
              name="website"
              value={companyInfo.website}
              onChange={handleCompanyInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Globe size={16} className="text-gray-500" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Industry"
              name="industry"
              value={companyInfo.industry}
              onChange={handleCompanyInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}>
              <InputLabel id="business-type-label">Business Type</InputLabel>
              <Select
                labelId="business-type-label"
                id="business-type"
                name="businessType"
                value={companyInfo.businessType}
                onChange={handleCompanyInfoChange}
                label="Business Type">
                <MenuItem value="proprietorship">Proprietorship</MenuItem>
                <MenuItem value="partnership">Partnership</MenuItem>
                <MenuItem value="private_limited">Private Limited</MenuItem>
                <MenuItem value="public_limited">Public Limited</MenuItem>
                <MenuItem value="llp">LLP</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Year Established"
              name="yearEstablished"
              value={companyInfo.yearEstablished}
              onChange={handleCompanyInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}>
              <InputLabel id="employees-label">Number of Employees</InputLabel>
              <Select
                labelId="employees-label"
                id="employees"
                name="numberOfEmployees"
                value={companyInfo.numberOfEmployees}
                onChange={handleCompanyInfoChange}
                label="Number of Employees">
                <MenuItem value="1-10">1-10</MenuItem>
                <MenuItem value="11-50">11-50</MenuItem>
                <MenuItem value="51-100">51-100</MenuItem>
                <MenuItem value="101-500">101-500</MenuItem>
                <MenuItem value="500+">500+</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </div>

      {/* Business Address Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex items-center mb-4">
          <MapPin size={20} className="text-[#5652B0] mr-2" />
          <ProductSection data={"Business Address"} />
        </div>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, fontFamily: "Outfit" }}>
          Update your company's registered address and location details
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="Street Address"
              name="street"
              value={addressInfo.street}
              onChange={handleAddressInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="City"
              name="city"
              value={addressInfo.city}
              onChange={handleAddressInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="State"
              name="state"
              value={addressInfo.state}
              onChange={handleAddressInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Postal Code"
              name="pincode"
              value={addressInfo.pincode}
              onChange={handleAddressInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Country"
              name="country"
              value={addressInfo.country}
              onChange={handleAddressInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
        </Grid>
      </div>

      {/* Bank Details Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex items-center mb-4">
          <FileText size={20} className="text-[#5652B0] mr-2" />
          <ProductSection data={"Bank Details"} />
        </div>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, fontFamily: "Outfit" }}>
          Update your company's banking information for payments and
          transactions
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Account Name"
              name="accountName"
              value={bankInfo.accountName}
              onChange={handleBankInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Account Number"
              name="accountNumber"
              value={bankInfo.accountNumber}
              onChange={handleBankInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Bank Name"
              name="bankName"
              value={bankInfo.bankName}
              onChange={handleBankInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Branch Name"
              name="branchName"
              value={bankInfo.branchName}
              onChange={handleBankInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="IFSC Code"
              name="ifscCode"
              value={bankInfo.ifscCode}
              onChange={handleBankInfoChange}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}>
              <InputLabel id="account-type-label">Account Type</InputLabel>
              <Select
                labelId="account-type-label"
                id="account-type"
                name="accountType"
                value={bankInfo.accountType}
                onChange={handleBankInfoChange}
                label="Account Type">
                <MenuItem value="savings">Savings</MenuItem>
                <MenuItem value="current">Current</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 mt-6">
        <Button
          variant="outlined"
          sx={{
            borderColor: "#5652B0",
            color: "#5652B0",
            textTransform: "none",
            fontFamily: "Outfit",
            "&:hover": {
              borderColor: "#4A47A3",
              backgroundColor: "rgba(86, 82, 176, 0.04)",
            },
          }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#5652B0",
            color: "white",
            textTransform: "none",
            fontFamily: "Outfit",
            "&:hover": {
              backgroundColor: "#4A47A3",
            },
          }}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default CompanySettings;
